import {
	useSelectCofCategories,
	useSelectLofCategories,
} from '@Selectors/Risk.selectors';
import { Category } from '@Types/category.types';
import { GridColDef } from '@innovyze/stylovyze';
import React, { useMemo } from 'react';
import { useGlobalization } from '@Translations/useGlobalization';
import { DriverScoreCell } from './RenderCell/DriverScoreCell';
import { useResultsGridFlags } from './useResultsGridFlags';

type ScoreType = 'LOF' | 'COF';
type TFn = ReturnType<typeof useGlobalization>['t'];

// Literal keys so the i18next parser can extract them; names are user data
// and go in as interpolation values, never as keys.
const redesignHeader = (
	t: TFn,
	scoreType: ScoreType,
	raw: boolean,
	category: string,
	component: string,
): string => {
	const o = { component, category };
	if (scoreType === 'LOF') {
		return raw
			? t('{{component}} ({{category}} LoF, raw)', o)
			: t('{{component}} ({{category}} LoF)', o);
	}
	return raw
		? t('{{component}} ({{category}} CoF, raw)', o)
		: t('{{component}} ({{category}} CoF)', o);
};

const createComponentScoreGridRow = (
	field: string,
	headerName: string,
	isRedesign: boolean,
): GridColDef => ({
	field,
	headerName,
	flex: 1,
	minWidth: 150,
	sortable: true,
	filterable: false,
	...(isRedesign && {
		renderCell: params =>
			React.createElement(DriverScoreCell, { value: params.value }),
	}),
});

const createCategoryComponents = (
	category: Category,
	scoreType: ScoreType,
	scoreTypeFE: string,
	isRedesign: boolean,
	t: TFn,
) =>
	category?.components
		?.filter(component => component.name)
		?.flatMap(component => [
			createComponentScoreGridRow(
				`${scoreType}_${category.name}_${component.name}`,
				isRedesign
					? redesignHeader(
							t,
							scoreType,
							false,
							category.name,
							component.name,
					  )
					: `${scoreTypeFE} ${category.name}-${component.name}`,
				isRedesign,
			),
			createComponentScoreGridRow(
				`${scoreType}Raw_${category.name}_${component.name}`,
				isRedesign
					? redesignHeader(
							t,
							scoreType,
							true,
							category.name,
							component.name,
					  )
					: `${scoreTypeFE}Raw ${category.name}-${component.name}`,
				isRedesign,
			),
		]);

export default function useGetWeightedScores() {
	const { t } = useGlobalization();
	const { isRedesign } = useResultsGridFlags();
	const lofCategories = useSelectLofCategories();
	const cofCategories = useSelectCofCategories();

	const lofScores = useMemo(
		() =>
			lofCategories
				.filter(category => category.components.length > 0)
				.flatMap(category =>
					createCategoryComponents(
						category,
						'LOF',
						t('LoF'),
						isRedesign,
						t,
					),
				),
		[lofCategories, isRedesign, t],
	);

	const cofScores = useMemo(
		() =>
			cofCategories
				.filter(category => category.components.length > 0)
				.flatMap(category =>
					createCategoryComponents(
						category,
						'COF',
						t('CoF'),
						isRedesign,
						t,
					),
				),
		[cofCategories, isRedesign, t],
	);

	return { lofScores, cofScores };
}
